// The property page in the pack's format (Neil, 10 Oct 2026: "populate the details page on console
// in the same format", the pack in 00-initial-commercial-intelligence/pack/render.py). Same numbered
// sections, in the same order, as cards. The parts that come from the AI research show skeleton
// placeholders until our team has approved it in Slack (aboutStatus), then fill in.
//
//   SectionHead          the card header every section uses: icon, "Section N", title, subtitle
//   ResearchPlaceholder  shimmering lines and where the research is, until it's approved
//   Opportunity          Section 1: the opportunity in one page
//   PropertyFacts        Section 2: about the property (the building's facts and the research's)
//   WhoDecides           Section 6: who decides
//   PropertyResearch     Section 11: property research
//   GrantsFunding        Section 12: grants and funding
//
// Never names where any of it comes from (CLAUDE.md).
//
// Private commercial (BUILD-PLAN-PRIVATE-COMMERCIAL.md PC5.3): the summary's rating and renewables
// lines only where there's something on record; the data badge says "Estimated" in neutral words and
// never "submission overdue" for a private property; public-only grant reasons only for public
// buildings; Section 2 lists the businesses under the roof (property.units) and the owner
// (property.owner) with its licence's attribution in small print.
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Sparkles,
  Building2,
  Users,
  Search,
  HandCoins,
  Clock,
  Hourglass,
  Leaf,
  Sun,
  KeyRound,
  ShoppingBag,
  Scale,
  SearchX,
  History,
  Home,
  Hammer,
  ScrollText,
  BadgeCheck,
} from "lucide-react";
import type { AboutFacts, AboutStatus, OwnedBuildingDetail } from "../api";
import { BuildingMap } from "./BuildingMap";
import { estimateWords } from "./packFormat";

const gbp = (n: number) => `${n < 0 ? "−" : ""}£${Math.abs(Math.round(n)).toLocaleString("en-GB")}`;
const k = (n: number) => (Math.abs(n) >= 10000 ? `${n < 0 ? "−" : ""}£${Math.round(Math.abs(n) / 1000).toLocaleString("en-GB")}k` : gbp(n));
const mwh = (kwh: number) => `${Math.round(kwh / 1000).toLocaleString("en-GB")} MWh`;
const month = (ym?: string | null) => {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "numeric" });
};

/** A year ("2019") as it is; a year and month as "Nov 2019". */
const when = (d: string) => (/^\d{4}$/.test(d) ? d : month(d));

/** The header every section card uses: the lime icon badge, "Section N", the title and a line under it. */
export function SectionHead({
  id,
  kicker,
  icon: Icon,
  title,
  sub,
  children,
}: {
  id: string;
  kicker: string;
  icon: LucideIcon;
  title: string;
  sub?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="airoof-head">
      <span className="airoof-mark" aria-hidden="true">
        <Icon size={20} />
      </span>
      <div className="airoof-title">
        <p className="pk-kicker">{kicker}</p>
        <h2 id={id}>{title}</h2>
        {sub && <p className="muted">{sub}</p>}
      </div>
      {children}
    </header>
  );
}

// What the placeholder says, by where the research is
const WAITING: Record<AboutStatus, string> = {
  waiting: "Our research starts once our team has checked the roof.",
  checking: "Our team is checking the research. It appears here once approved.",
  approved: "Nothing found.",
  unavailable: "We couldn't complete the research for this property.",
};

/** Skeleton lines (shimmering while the research is on its way) and a note saying where it is. */
export function ResearchPlaceholder({ status, lines = 4 }: { status?: AboutStatus | null; lines?: number }) {
  const s = status ?? "waiting";
  const live = s === "waiting" || s === "checking";
  // Varied widths so the placeholder reads as text
  const widths = [92, 78, 85, 64, 88, 70, 80, 58];
  return (
    <div className={`pk-skel${live ? " is-live" : ""}`} aria-busy={live}>
      <div className="pk-skel-lines" aria-hidden="true">
        {Array.from({ length: lines }, (_, i) => (
          <span key={i} style={{ width: `${widths[i % widths.length]}%` }} />
        ))}
      </div>
      <p className="pk-skel-note">
        <Hourglass size={14} aria-hidden="true" /> {WAITING[s]}
      </p>
    </div>
  );
}

// Research done before these areas were asked about (depth 1): say so, rather than "couldn't find"
const OLDER = "This wasn't part of this property's research: it was done before we added it.";

/** A labelled list, or the placeholder while the research isn't approved, or what wasn't found, plainly. */
function ResearchList({
  title,
  icon: Icon,
  about,
  status,
  items,
  empty,
}: {
  title: string;
  icon: LucideIcon;
  about?: AboutFacts | null;
  status?: AboutStatus | null;
  items: ReactNode[];
  /** Said when the research found nothing for this panel, e.g. "We couldn't find any planning applications for this building." */
  empty?: string;
}) {
  return (
    <div className="about-list pk-panel">
      <h3>
        <Icon size={16} aria-hidden="true" /> {title}
      </h3>
      {!about ? (
        <ResearchPlaceholder status={status} lines={3} />
      ) : items.length ? (
        <ul>{items}</ul>
      ) : (
        <p className="pk-notfound">{empty ?? `We couldn't find anything on this in public sources.`}</p>
      )}
    </div>
  );
}

/** "Why now" points: dated where the research gave a date. */
function whyNowItems(about?: AboutFacts | null) {
  return (about?.whyNow ?? []).map((w) => (
    <li key={w.point}>
      {w.date && <span className="about-date">{month(w.date)}</span>}
      {w.point}
    </li>
  ));
}


/** Solar already contracted for the roof, from a "why now" point (as the report's `contracted`). */
export function solarContracted(about?: AboutFacts | null) {
  return Boolean(about?.existingSolar) || (about?.whyNow ?? []).some((w) => /\bsolar\b.*\b(contract|install|award)/i.test(w.point));
}

/** Why each funding scheme fits this building (the report's "Why it fits here", render.py `fit`). */
function grantFit(key: string, b: OwnedBuildingDetail): string {
  const about = b.about;
  const r = b.energy?.model?.recommended;
  const ptype = (b.property.buildingType || "building").toLowerCase();
  // Public sector schemes' reasons only for public buildings (private ones get the private routes)
  const isPublic = b.property.ownership !== "private";
  const received = (about?.funding ?? []).some((x) => (x.scheme || "").includes("Great British Energy") || (x.scheme || "").toLowerCase().includes("mayoral"));
  const fits: Record<string, string> = {
    psds: `${about?.owner || "The owner"} is a public body; a project pairing solar with heat pumps scores best.`,
    gbe: `Public buildings like this ${ptype} have been funded through regional rounds.${received ? " This building has already received it (Section 11)." : ""}`,
    "salix-loans": `A ${r?.payback ?? "—"}-year payback repays an interest-free loan from the savings.`,
    cif: "Academies can include energy works in their annual bid.",
    community: `A ${ptype} open to the public suits a community share offer.`,
    seg: `Worth about £${Math.round(r?.flows?.[0]?.export ?? 0).toLocaleString("en-GB")} a year in export income for the recommended system.`,
  };
  if (!isPublic && ["psds", "gbe", "cif"].includes(key)) return "";
  return fits[key] ?? "";
}

/** Section 1: the opportunity in one page. */
export function Opportunity({ b }: { b: OwnedBuildingDetail }) {
  const e = b.energy;
  const t = e?.today;
  const m = e?.model;
  const r = m?.recommended;
  const roof = b.site?.roof;
  const about = b.about;
  const p = b.property;
  if (!e || !t) return null;
  const occ = p.buildingType ? `the ${p.buildingType.toLowerCase()}` : "the building";
  const profile = m?.profile.label ?? "";
  const es = about?.existingSolar;

  // Why this building: from the energy figures, the model and the roof (no research needed)
  const why: ReactNode[] = [
    <li key="use">
      <b>{mwh(t.electricityKwh)} of electricity a year</b> ({e.basis === "benchmark" ? "estimated from its size and use" : e.estimated ? "estimated" : "actual use"}), about {k(t.electricity)} at {e.prices.importPence}p/kWh.
    </li>,
    profile && r ? (
      <li key="hours">
        <b>{profile.split(":")[0]}:</b> {profile.split(":").slice(1).join(":").trim()}, so {r.selfUse}% of the recommended system's output is used on site.
      </li>
    ) : null,
    roof ? (
      <li key="roof">
        <b>
          {roof.flatPct}% flat roof{roof.roofM2 ? ` of about ${roof.roofM2.toLocaleString("en-GB")} m²` : ""}
        </b>
        {roof.sunnyPct != null ? `, ${roof.sunnyPct}% of it in strong sun` : ""}
        {roof.capacityKwp != null ? `, room for up to ${roof.capacityKwp} kWp` : ""}.
      </li>
    ) : null,
    e.ratings?.asRun ? (
      <li key="rating">
        <b>Efficiency rating {e.ratings.asRun}</b> as run{e.ratings.asRunScore != null ? ` (${e.ratings.asRunScore}, where 100 is typical)` : ""}.
      </li>
    ) : null,
    // Only where there's a record either way (nothing on record for an estimate)
    e.renewablesRecorded != null ? (
      <li key="renew">
        <b>{e.renewablesRecorded ? "On-site renewable generation is recorded." : "No on-site renewable generation recorded."}</b>
      </li>
    ) : null,
  ].filter(Boolean);

  // The recommended approach: richer once the research says who runs it, or finds solar already there
  const steps: string[] = [];
  if (es || solarContracted(about))
    steps.push("check the solar already contracted for this roof first: if it's installed, the opportunity is extra capacity, battery storage or the next site");
  if (about?.operator) steps.push(`contact ${about.operator} about the roof and its energy plans`);
  if (r)
    steps.push(
      `propose a ${r.kwp} kWp system${m?.phase1 ? ` (or ${m.phase1.kwp} kWp as a lower-capital start)` : ""}, funded as capital, through a grant or loan (Section 12), or by a third party through a power purchase agreement`,
    );

  return (
    <section className="card eo" aria-labelledby="pk-opp">
      <SectionHead id="pk-opp" kicker="Section 1" icon={Sparkles} title="The opportunity in one page" />
      {r && (
        <p className="pk-lede">
          {p.buildingType ?? "The building"} using {e.estimated ? "an estimated " : ""}
          {mwh(t.electricityKwh)} of electricity a year. A {r.kwp} kWp system would generate {mwh(r.gen)} a year, {r.selfUse}% of
          it used on site, covering {r.covers}% of {occ}'s electricity{r.payback != null ? ` and paying back in ${r.payback} years` : ""}.
        </p>
      )}
      {/* The pack's eight headline figures (no summary strip of the "Before you start" items here:
          Neil, 13 Oct 2026; they're in their own section above) */}
      <div className="eo-stats">
        <div>
          <b>{k(t.electricity)}</b>
          <span>electricity a year at {e.prices.importPence}p/kWh</span>
        </div>
        {/* An estimate has no trend or renewables record: say how it was estimated and the floor area instead */}
        {e.basis === "benchmark" && e.changePct == null ? (
          <div>
            <b>Estimated</b>
            <span>electricity use, from the building's size and use</span>
          </div>
        ) : (
          <div>
            <b>{e.changePct != null ? `${e.changePct > 0 ? "+" : ""}${e.changePct}%` : "—"}</b>
            <span>a year electricity change, last 3 years</span>
          </div>
        )}
        {e.renewablesRecorded != null ? (
          <div>
            <b>{e.renewablesRecorded ? "Recorded" : "0 kWh"}</b>
            <span>on-site renewables on record</span>
          </div>
        ) : (
          <div>
            <b>{p.floorM2 ? `${p.floorM2.toLocaleString("en-GB")} m²` : "—"}</b>
            <span>floor area</span>
          </div>
        )}
        <div>
          <b>{roof?.maxPanels != null ? roof.maxPanels.toLocaleString("en-GB") : "—"}</b>
          <span>panel positions on the roof</span>
        </div>
        {r && (
          <>
            <div className="is-dark">
              <b>{k(r.capex)}</b>
              <span>install, {r.kwp} kWp, ex VAT</span>
            </div>
            <div className="is-dark">
              <b>{k(r.year1)}</b>
              <span>saved in year one</span>
            </div>
            <div className="is-dark">
              <b>{r.payback != null ? `${r.payback} yrs` : "—"}</b>
              <span>simple payback{r.irr != null ? ` · ${r.irr}% IRR` : ""}</span>
            </div>
            <div className="is-dark">
              <b>{r.co2Year} t</b>
              <span>CO₂ avoided a year</span>
            </div>
          </>
        )}
      </div>

      <div className="about-two">
        <div className="about-list pk-panel">
          <h3>
            <Building2 size={16} aria-hidden="true" /> Why this building
          </h3>
          <ul>{why}</ul>
        </div>
        <ResearchList title="Why now" icon={Clock} about={about} status={b.aboutStatus} items={whyNowItems(about)} />
      </div>

      {steps.length > 0 && (
        <div className="pk-callout">
          <BadgeCheck size={16} aria-hidden="true" />
          <span>
            <b>Recommended approach:</b> {steps.join("; ")}.
          </span>
        </div>
      )}
      <p className="airoof-foot muted">Costs and returns are estimates for prospecting, not a quotation. See Section 13 for every assumption.</p>
    </section>
  );
}

const OWNER_TYPES: Record<string, string> = {
  council: "Council",
  nhs: "NHS",
  "academy trust": "Academy trust",
  school: "School",
  university: "University",
  company: "Company",
  charity: "Charity",
};

/** Section 2: about the property, its facts and (once approved) who owns and runs it. */
export function PropertyFacts({ b }: { b: OwnedBuildingDetail }) {
  const p = b.property;
  const about = b.about;
  const fmt = (n?: number | null, unit = "") => (n == null ? "—" : `${n.toLocaleString("en-GB")}${unit}`);
  // Overdue is a public building's flag only: never shown for a private one
  const overdue = Boolean(p.overdue) && p.ownership !== "private";
  const estimated = p.electricityData === "estimated";
  // Several businesses under one roof, and the owner (once ordered; private only)
  const units = p.units ?? [];
  const owner = p.owner;
  const ours: [string, ReactNode][] = [
    ["Address", [...(p.addressLines ?? []), p.town, p.postcode].filter(Boolean).join(", ")],
    ["Type", p.buildingType ?? "—"],
    ["Floor area", fmt(p.floorM2, " m²")],
    ["Local authority", p.district ?? "—"],
    [estimated ? "Electricity a year (estimated)" : "Electricity a year", fmt(p.electricityKwh, " kWh")],
    ...(b.site?.grid?.covered && b.site.grid.operator ? ([["Grid", `${b.site.grid.operator} area`]] as [string, ReactNode][]) : []),
    [
      "Data",
      <span className={`find-data find-data-${overdue ? "overdue" : p.electricityData}`} title={estimated && p.estimate ? estimateWords(p) : undefined}>
        {p.electricityData === "actual" ? (overdue ? "Actual data · submission overdue" : "Actual data") : p.estimate ? "Estimated from size and use" : "Estimated"}
      </span>,
    ],
  ];
  const researched = about
    ? (
        [
          ["Owner", about.owner ? `${about.owner}${about.ownerType && OWNER_TYPES[about.ownerType] ? ` (${OWNER_TYPES[about.ownerType]})` : ""}` : null],
          ["Run by", about.operator],
          ["Opening hours", about.openingHours],
          ["Visitors or pupils", about.visitors],
          ["Opened", about.opened],
          ["Heating", b.energy?.ratings?.design?.heating || about.heating],
        ] as [string, string | null][]
      ).filter((f): f is [string, string] => Boolean(f[1]))
    : [];
  return (
    <section className="card eo" aria-labelledby="pk-about">
      <SectionHead id="pk-about" kicker="Section 2" icon={Building2} title="About the property" />
      <BuildingMap orderId={b.orderId} />
      <div className="about-two pk-facts">
        <dl className="building-facts">
          {ours.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
        <div>
          {about ? (
            <dl className="building-facts">
              {researched.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <div className="pk-panel">
              <h3 className="pk-sub">Owner, operator and use</h3>
              <ResearchPlaceholder status={b.aboutStatus} lines={5} />
            </div>
          )}
        </div>
      </div>
      {/* The owner (private, once ordered), with its licence's attribution in small print */}
      {owner && (
        <div className="pk-panel">
          <h3 className="pk-sub">
            <KeyRound size={16} aria-hidden="true" className="pk-h3-ico" /> Owner
          </h3>
          <p>
            <b>{owner.name}</b>
            {[
              owner.companyNumber ? `company number ${owner.companyNumber}` : null,
              owner.tenure ? owner.tenure.toLowerCase() : null, // "freehold" or "leasehold"
              owner.overseas ? "registered overseas" : null,
            ]
              .filter(Boolean)
              .map((x) => ` · ${x}`)
              .join("")}
          </p>
          {owner.attribution && <p className="muted pk-attrib">{owner.attribution}</p>}
        </div>
      )}
      {/* Several businesses under one roof: each one's use, floor area and estimated electricity */}
      {units.length > 0 && (
        <div className="pk-panel">
          <h3 className="pk-sub">
            <Users size={16} aria-hidden="true" className="pk-h3-ico" /> Businesses in this building
          </h3>
          <div className="eo-table-wrap">
            <table className="eo-table">
              <thead>
                <tr>
                  <th>Business</th>
                  <th>Use</th>
                  <th>Floor area</th>
                  <th>Electricity a year (estimated)</th>
                </tr>
              </thead>
              <tbody>
                {units.map((u, i) => (
                  <tr key={`${u.name}-${i}`}>
                    <td>{u.name}</td>
                    <td>{u.use}</td>
                    <td>{fmt(u.floorM2, " m²")}</td>
                    <td>{fmt(u.elecKwh != null ? Math.round(u.elecKwh) : null, " kWh")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted eo-note">The roof usually belongs to the landlord: a solar project needs their consent as well as the occupiers'.</p>
        </div>
      )}
      {about?.description && <p className="airoof-summary">{about.description}</p>}
      {about?.operatorNote && <p className="about-note">{about.operatorNote}</p>}
    </section>
  );
}

const initials = (name: string) =>
  name
    .replace(/^(Cllr|Dr|Mr|Mrs|Ms|Prof)\.?\s+/i, "")
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

// The roles every solar project needs, named or not (as in the pack)
const ROLES = [
  { role: "Estates or facilities manager", what: "Roof access, plant and the electricity supply" },
  { role: "Site manager", what: "Day-to-day site contact" },
];

/** Section 6: who decides. Named people only from the approved research; the usual roles always. */
export function WhoDecides({ b }: { b: OwnedBuildingDetail }) {
  const about = b.about;
  const people = about?.people ?? [];
  return (
    <section className="card eo" aria-labelledby="pk-who">
      <SectionHead
        id="pk-who"
        kicker="Section 6"
        icon={Users}
        title="Who decides"
        sub={`${about ? `${about.owner || "The owner"} owns the building${about.operator && about.operator !== about.owner ? ` and ${about.operator} runs it` : ""}. ` : ""}A solar project needs the owner's agreement and whoever pays the energy bills. Profiles are from public sources only; confirm roles before contact.`}
      />
      {!about ? (
        <div className="about-people">
          {[0, 1].map((i) => (
            <div key={i} className="about-person pk-person-skel">
              <span className="about-initials pk-skel-dot" aria-hidden="true" />
              <ResearchPlaceholder status={b.aboutStatus} lines={3} />
            </div>
          ))}
        </div>
      ) : (
        people.length > 0 && (
          <div className="about-people">
            {people.map((p) => (
              <div key={p.name} className="about-person">
                <span className="about-initials" aria-hidden="true">
                  {initials(p.name)}
                </span>
                <div>
                  <b>{p.name}</b> <span className="about-verify">To verify</span>
                  <div className="about-role">{p.role}</div>
                  {p.organisation && (
                    <div className="muted about-org">
                      {p.organisation}
                      {p.seen ? ` · named ${month(p.seen)}` : ""}
                    </div>
                  )}
                  {p.why && <p className="about-why">{p.why}</p>}
                </div>
              </div>
            ))}
          </div>
        )
      )}
      <div className="about-people pk-roles">
        {ROLES.map((r) => (
          <div key={r.role} className="about-person">
            <span className="about-initials pk-role-mark" aria-hidden="true">
              ?
            </span>
            <div>
              <b>{r.role}</b>
              <div className="about-role">{r.what}</div>
              <p className="about-why">Not named publicly: ask at reception or through the organisation's main switchboard.</p>
            </div>
          </div>
        ))}
      </div>
      <p className="muted about-gdpr">No personal contact details: use the organisation's published channels, and follow UK GDPR and PECR.</p>
    </section>
  );
}

/** Section 11: property research, all from the approved research (history, roof, renewables, procurement, funding, planning). */
export function PropertyResearch({ b }: { b: OwnedBuildingDetail }) {
  const about = b.about;
  const status = b.aboutStatus;
  return (
    <section className="card eo" aria-labelledby="pk-research">
      <SectionHead
        id="pk-research"
        kicker="Section 11"
        icon={Search}
        title="Property research"
        sub="Public information about the building and the organisations behind it, gathered by our research and checked by our team."
      />
      <div className="about-two">
        <ResearchList title="Why now" icon={Clock} about={about} status={status} items={whyNowItems(about)} empty="We couldn't find anything that makes solar especially timely here." />
        <ResearchList
          title="Energy and carbon position"
          icon={Leaf}
          about={about}
          status={status}
          items={(about?.energyPlans ?? []).map((x) => (
            <li key={x.point}>
              {x.date && <span className="about-date">{month(x.date)}</span>}
              {x.point}
            </li>
          ))}
          empty="We couldn't find any net-zero targets, energy plans or reports for the owner or occupier."
        />
      </div>
      {/* Everything renewable or low-carbon at the building, past to planned */}
      <ResearchList
        title="Renewables and low-carbon"
        icon={Sun}
        about={about}
        status={status}
        items={(about?.renewables ?? []).map((r) => (
          <li key={r.type + r.detail} className="pk-renew">
            <span className={`pk-status is-${r.status}`}>{r.status}</span> <b>{r.type}</b>
            {r.size ? ` · ${r.size}` : ""}
            {r.date ? ` · ${month(r.date)}` : ""}
            {r.contractor ? ` · ${r.contractor}` : ""}
            <div>{r.detail}</div>
          </li>
        ))}
      />
      <div className="about-two">
        <div className="about-list pk-panel">
          <h3>
            <Home size={16} aria-hidden="true" /> The roof
          </h3>
          {!about ? (
            <ResearchPlaceholder status={status} lines={3} />
          ) : about.roof ? (
            <dl className="building-facts pk-roof">
              {(
                [
                  ["Age", about.roof.age],
                  ["Covering", about.roof.covering],
                  ["Condition", about.roof.condition],
                  ["Last works", about.roof.lastWorks],
                ] as [string, string | null][]
              )
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <div key={k}>
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
            </dl>
          ) : (
            <p className="pk-notfound">We couldn't find the roof's age, covering or condition in public sources: check them at the survey.</p>
          )}
        </div>
        <ResearchList
          title="History"
          icon={History}
          about={about}
          status={status}
          items={(about?.history ?? []).map((h) => (
            <li key={(h.date ?? "") + h.event}>
              {h.date && <span className="about-date">{when(h.date)}</span>}
              {h.event}
            </li>
          ))}
        />
      </div>
      <div className="about-two">
        <ResearchList
          title="Procurement at the site"
          icon={ScrollText}
          about={about}
          status={status}
          items={(about?.procurement ?? []).map((x) => (
            <li key={x.what}>
              {x.date && <span className="about-date">{month(x.date)}</span>}
              {x.what}
              {[x.status, x.value, x.supplier].filter(Boolean).length ? ` (${[x.status, x.value, x.supplier].filter(Boolean).join(" · ")})` : ""}
            </li>
          ))}
        />
        <ResearchList
          title="Funding received or applied for"
          icon={HandCoins}
          about={about}
          status={status}
          items={(about?.funding ?? []).map((f) => (
            <li key={f.scheme}>
              {f.date && <span className="about-date">{month(f.date)}</span>}
              <b>{f.scheme}</b>
              {f.status ? ` (${f.status})` : ""}
              {f.amount ? ` · ${f.amount}` : ""}
              {f.what ? `: ${f.what}` : ""}
            </li>
          ))}
        />
      </div>
      <div className="about-two">
        <ResearchList
          title="Recent or planned works"
          icon={Hammer}
          about={about}
          status={status}
          items={about?.recentWorks ? [<li key="w">{about.recentWorks}</li>] : []}
          empty="We couldn't find any recent or planned works at the building."
        />
        <ResearchList
          title="Planning history and new electricity use"
          icon={ScrollText}
          about={about}
          status={status}
          items={[
            ...(about?.planning ?? []).map((x) => (
              <li key={x.what}>
                {x.date && <span className="about-date">{month(x.date)}</span>}
                {x.what}
                {x.reference ? ` (ref ${x.reference})` : ""}
                {x.status ? ` (${x.status})` : ""}
              </li>
            )),
            ...(about?.electrification ?? []).map((x) => <li key={x.point}>{x.point}</li>),
          ]}
          empty="We couldn't find any planning applications or plans to add electric load for this building."
        />
      </div>
      {/* Who holds the building, how they buy, their energy contract, and the owner's company */}
      <div className="about-two">
        <ResearchList
          title="Ownership and tenure"
          icon={KeyRound}
          about={about}
          status={status}
          items={[
            about?.tenure?.holding ? (
              <li key="h">
                <b>{about.tenure.holding === "leasehold" ? "Leasehold" : "Freehold"}</b>
                {about.tenure.landlord ? ` · landlord ${about.tenure.landlord}` : ""}
                {about.tenure.leaseEnds ? ` · lease ends ${about.tenure.leaseEnds}` : ""}
              </li>
            ) : null,
            about?.tenure?.detail ? <li key="d">{about.tenure.detail}</li> : null,
            about?.companyHealth ? (
              <li key="c">
                <b>Company:</b> {[about.companyHealth.status, about.companyHealth.accountsOverdue === true ? "accounts overdue" : about.companyHealth.accountsOverdue === false ? "accounts up to date" : null]
                  .filter(Boolean)
                  .join(" · ")}
                {about.companyHealth.detail ? `. ${about.companyHealth.detail}` : ""}
              </li>
            ) : null,
          ].filter(Boolean)}
          empty={about?.depth === 1 ? OLDER : "We couldn't find whether the building is owned or leased, or who the landlord is. Ask at the first conversation: a power purchase agreement needs 15 to 25 years of tenure."}
        />
        <ResearchList
          title="How they buy, and their energy contract"
          icon={ShoppingBag}
          about={about}
          status={status}
          items={[
            about?.buying?.mustTender != null ? (
              <li key="t">{about.buying.mustTender ? "They must run a tender or buy through a framework." : "They can buy directly, without a tender."}</li>
            ) : null,
            about?.buying?.frameworks.length ? <li key="f">Frameworks: {about.buying.frameworks.join(", ")}</li> : null,
            about?.buying?.budgetCycle ? <li key="b">Budgets: {about.buying.budgetCycle}</li> : null,
            about?.buying?.detail ? <li key="bd">{about.buying.detail}</li> : null,
            about?.energyContract ? (
              <li key="e">
                <b>Energy contract:</b>{" "}
                {[about.energyContract.supplier, about.energyContract.framework, about.energyContract.renews ? `renews ${month(about.energyContract.renews)}` : null]
                  .filter(Boolean)
                  .join(" · ")}
                {about.energyContract.detail ? `${[about.energyContract.supplier, about.energyContract.framework, about.energyContract.renews].some(Boolean) ? ". " : ""}${about.energyContract.detail}` : ""}
              </li>
            ) : null,
          ].filter(Boolean)}
          empty={about?.depth === 1 ? OLDER : "We couldn't find how they buy or when their energy contract renews. Ask: a renewal is a natural moment to add on-site generation."}
        />
      </div>
      <ResearchList
        title="Compliance pressures"
        icon={Scale}
        about={about}
        status={status}
        items={(about?.compliance ?? []).map((c) => (
          <li key={c.scheme + c.detail}>
            <span className={`pk-status is-${c.applies === "yes" ? "contracted" : c.applies === "likely" ? "planned" : "removed"}`}>{c.applies}</span> <b>{c.scheme === "other" ? "Other" : c.scheme}</b>{" "}
            <div>{c.detail}</div>
          </li>
        ))}
        empty={about?.depth === 1 ? OLDER : "We couldn't find any compliance obligations (such as MEES, ESOS or SECR) that apply to this building's owner or occupier."}
      />
      <p className="muted eo-note">
        MEES: a let building needs an energy rating of E or better, rising. ESOS: large companies audit their energy use. SECR: large companies report their energy and
        carbon each year.
      </p>
      {/* What the research looked for and couldn't find, plainly */}
      {about && about.notFound && about.notFound.length > 0 && (
        <div className="about-list pk-panel pk-notfound-panel">
          <h3>
            <SearchX size={16} aria-hidden="true" /> What we couldn't find
          </h3>
          <ul>
            {about.notFound.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </div>
      )}
      <p className="airoof-foot muted">From public information, checked by our team. Confirm anything you rely on before you contact them.</p>
    </section>
  );
}

/** Section 12: grants and funding: the schemes that may apply (at once) and what it has received (research). */
export function GrantsFunding({ b }: { b: OwnedBuildingDetail }) {
  const s = b.site;
  if (!s) return null;
  const received = b.about?.funding ?? [];
  return (
    <section className="card eo" aria-labelledby="pk-grants">
      <SectionHead
        id="pk-grants"
        kicker="Section 12"
        icon={HandCoins}
        title="Grants and funding"
        sub="Schemes that may apply to this building, how each works and what to do, and any funding it has already received."
      />
      {/* Already received or applied for: from the research */}
      <div className="pk-panel">
        <h3 className="pk-sub">Funding this building has received or applied for</h3>
        {!b.about ? (
          <ResearchPlaceholder status={b.aboutStatus} lines={2} />
        ) : received.length ? (
          <ul className="site-list">
            {received.map((f) => (
              <li key={f.scheme} className="is-ok">
                <b>{f.scheme}</b>
                {f.status ? ` (${f.status})` : ""}
                {f.amount ? ` · ${f.amount}` : ""}
                {f.date ? ` · ${month(f.date)}` : ""}
                {f.what ? `: ${f.what}` : ""}
              </li>
            ))}
          </ul>
        ) : (
          <p className="pk-notfound">We couldn't find any funding this building has received or applied for.</p>
        )}
      </div>
      <div className="pk-grants">
        {s.funding.map((f) => (
          <div key={f.key} className="pk-grant">
            <h3>{f.name}</h3>
            <p>{f.what}</p>
            <dl>
              <div>
                <dt>Who it's for</dt>
                <dd>{f.who}</dd>
              </div>
              {f.how && (
                <div>
                  <dt>How it works</dt>
                  <dd>{f.how}</dd>
                </div>
              )}
              {grantFit(f.key, b) && (
                <div>
                  <dt>Why it fits here</dt>
                  <dd>{grantFit(f.key, b)}</dd>
                </div>
              )}
              {f.todo && (
                <div className="pk-todo">
                  <dt>What to do</dt>
                  <dd>{f.todo}</dd>
                </div>
              )}
            </dl>
          </div>
        ))}
      </div>
      <p className="muted eo-note">Schemes open and close and their rules change: check the current round before relying on one. Our list was last checked {s.fundingCheckedOn}.</p>
    </section>
  );
}
